/**
 * 런타임 ERP 테마 Provider
 * - 기관 appearance ⊕ 개인 prefs ⊕ colorMode → ThemeProvider + :root CSS 변수
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { ThemeProvider } from 'styled-components';
import { theme as baseTheme, applyCssVariablesToDom } from 'styles/theme';
import { buildThemeFromAppearance } from 'styles/buildThemeFromAppearance';
import {
  loadTenantAppearance,
  loadPersonalAppearance,
  fetchTenantAppearance,
  fetchPersonalAppearance,
  resolveAppearance,
  saveTenantAppearance,
  savePersonalAppearance,
} from 'system/appearanceStore';
import { defaultAppearanceSettings } from 'styles/appearanceSlots';
import { ensureErpFontLoaded, normalizeFontSizeRootPx, stepFontSizeRootPx } from 'styles/erpFonts';
import { useAuth } from 'contexts/AuthContext';
import useTenantCode from 'hooks/useTenantCode';

const ErpThemeContext = createContext(null);

export function ErpThemeProvider({ children }) {
  const { user } = useAuth();
  const tenantCode = useTenantCode();
  const userId = user?.id || '';

  const [tenantAppearance, setTenantAppearance] = useState(() =>
    loadTenantAppearance(tenantCode)
  );
  const [personalAppearance, setPersonalAppearance] = useState(() =>
    loadPersonalAppearance(tenantCode, userId)
  );
  const [revision, setRevision] = useState(0);

  const reload = useCallback(() => {
    Promise.all([
      fetchTenantAppearance(tenantCode),
      fetchPersonalAppearance(tenantCode, userId),
    ]).then(([tenant, personal]) => {
      setTenantAppearance(tenant);
      setPersonalAppearance(personal);
      setRevision((n) => n + 1);
    });
  }, [tenantCode, userId]);

  useEffect(() => {
    reload();
  }, [reload]);

  const resolved = useMemo(
    () => resolveAppearance(tenantCode, userId),
    // revision으로 저장 직후 재계산
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tenantCode, userId, revision, tenantAppearance, personalAppearance]
  );

  const activeTheme = useMemo(
    () => buildThemeFromAppearance(resolved, baseTheme),
    [resolved]
  );

  useEffect(() => {
    applyCssVariablesToDom(activeTheme);
  }, [activeTheme]);

  useEffect(() => {
    ensureErpFontLoaded(resolved.fontFamilyId);
  }, [resolved.fontFamilyId]);

  const updateTenantAppearance = useCallback(
    (next) => {
      const saved = saveTenantAppearance(tenantCode, {
        ...defaultAppearanceSettings(),
        ...tenantAppearance,
        ...next,
      });
      setTenantAppearance(saved);
      setRevision((n) => n + 1);
      return saved;
    },
    [tenantCode, tenantAppearance]
  );

  const updatePersonalAppearance = useCallback(
    (next) => {
      const saved = savePersonalAppearance(tenantCode, userId, {
        ...personalAppearance,
        ...next,
      });
      setPersonalAppearance(saved);
      setRevision((n) => n + 1);
      return saved;
    },
    [tenantCode, userId, personalAppearance]
  );

  const setColorMode = useCallback(
    (mode) => {
      const m = mode === 'dark' ? 'dark' : 'light';
      // 개인 모드 우선 저장
      if (userId) {
        updatePersonalAppearance({ colorMode: m });
      } else {
        updateTenantAppearance({ colorMode: m });
      }
    },
    [userId, updatePersonalAppearance, updateTenantAppearance]
  );

  const setFontSizeRootPx = useCallback(
    (px) => {
      const n = normalizeFontSizeRootPx(px);
      if (userId) {
        updatePersonalAppearance({ fontSizeRootPx: n });
      } else {
        updateTenantAppearance({ fontSizeRootPx: n });
      }
    },
    [userId, updatePersonalAppearance, updateTenantAppearance]
  );

  const stepFontSize = useCallback(
    (dir) => {
      const cur = normalizeFontSizeRootPx(resolved.fontSizeRootPx);
      setFontSizeRootPx(stepFontSizeRootPx(cur, dir));
    },
    [resolved.fontSizeRootPx, setFontSizeRootPx]
  );

  const value = useMemo(
    () => ({
      theme: activeTheme,
      resolvedAppearance: resolved,
      tenantAppearance,
      personalAppearance,
      colorMode: resolved.colorMode || 'light',
      fontSizeRootPx: normalizeFontSizeRootPx(resolved.fontSizeRootPx),
      allowPersonalFontSize: tenantAppearance?.allowPersonalFontSize !== false,
      reload,
      updateTenantAppearance,
      updatePersonalAppearance,
      setColorMode,
      setFontSizeRootPx,
      stepFontSize,
    }),
    [
      activeTheme,
      resolved,
      tenantAppearance,
      personalAppearance,
      reload,
      updateTenantAppearance,
      updatePersonalAppearance,
      setColorMode,
      setFontSizeRootPx,
      stepFontSize,
    ]
  );

  return (
    <ErpThemeContext.Provider value={value}>
      <ThemeProvider theme={activeTheme}>{children}</ThemeProvider>
    </ErpThemeContext.Provider>
  );
}

export function useErpTheme() {
  const ctx = useContext(ErpThemeContext);
  if (!ctx) {
    throw new Error('useErpTheme must be used within ErpThemeProvider');
  }
  return ctx;
}

/** Provider 밖(로그인 등)에서도 안전하게 */
export function useErpThemeOptional() {
  return useContext(ErpThemeContext);
}
